import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  CalendarIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  TrophyIcon,
  AtSymbolIcon,
  ArrowRightIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, InlineSpinner } from '@/components/ui'
import { dashboardApi } from '@/api'
import type { DashboardNotification } from '@/api/dashboard'
import { formatDistanceToNow, parseISO, format } from 'date-fns'
import { it } from 'date-fns/locale'

const notificationIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationTriangleIcon,
  mention: AtSymbolIcon,
  reply: ChatBubbleLeftRightIcon,
  badge: TrophyIcon,
  course: AcademicCapIcon,
  event: CalendarIcon,
  taskforce: UserGroupIcon,
  clinic: InformationCircleIcon,
}

const notificationColors: Record<string, { bg: string; icon: string }> = {
  info: { bg: 'bg-blue-50', icon: 'text-blue-500' },
  success: { bg: 'bg-green-50', icon: 'text-green-500' },
  warning: { bg: 'bg-yellow-50', icon: 'text-yellow-500' },
  error: { bg: 'bg-red-50', icon: 'text-red-500' },
  mention: { bg: 'bg-purple-50', icon: 'text-purple-500' },
  reply: { bg: 'bg-indigo-50', icon: 'text-indigo-500' },
  badge: { bg: 'bg-yellow-50', icon: 'text-yellow-600' },
  course: { bg: 'bg-blue-50', icon: 'text-blue-600' },
  event: { bg: 'bg-purple-50', icon: 'text-purple-600' },
  taskforce: { bg: 'bg-orange-50', icon: 'text-orange-600' },
  clinic: { bg: 'bg-green-50', icon: 'text-green-600' },
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<DashboardNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const data = await dashboardApi.getNotifications({ page: 1 })
      setNotifications(data.results)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await dashboardApi.markAllNotificationsRead()
      setNotifications(notifications.map(n => ({ ...n, is_read: true })))
    } catch (error) {
      console.error('Error marking notifications as read:', error)
    }
  }

  const handleMarkRead = async (id: number) => {
    try {
      await dashboardApi.markNotificationRead(id)
      setNotifications(notifications.map(n =>
        n.id === id ? { ...n, is_read: true } : n
      ))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.is_read)
    : notifications

  const unreadCount = notifications.filter(n => !n.is_read).length

  if (loading) {
    return <InlineSpinner message="Caricamento notifiche..." />
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <BellIcon className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifiche</h1>
            <p className="text-gray-600">
              {unreadCount > 0 ? `${unreadCount} non lette` : 'Tutte lette'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Tutte
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Non lette
              {unreadCount > 0 && (
                <Badge variant="danger" size="sm" className="ml-2">
                  {unreadCount}
                </Badge>
              )}
            </button>
          </div>

          {/* Mark all read */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <CheckIcon className="h-4 w-4" />
              Segna tutte lette
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <Card className="divide-y divide-gray-100">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => {
            const Icon = notificationIcons[notification.type] || InformationCircleIcon
            const colors = notificationColors[notification.type] || notificationColors.info

            return (
              <div
                key={notification.id}
                className={`p-4 transition-all ${
                  notification.is_read ? 'bg-white' : 'bg-blue-50/30'
                } hover:bg-gray-50`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-full ${colors.bg} flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${colors.icon}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`font-medium ${notification.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <span className="h-2 w-2 bg-primary-500 rounded-full flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>
                        {formatDistanceToNow(parseISO(notification.created_at), {
                          addSuffix: true,
                          locale: it
                        })}
                      </span>
                      <span>
                        {format(parseISO(notification.created_at), 'dd MMM yyyy, HH:mm', { locale: it })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!notification.is_read && (
                      <button
                        onClick={() => handleMarkRead(notification.id)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Segna come letta"
                      >
                        <CheckIcon className="h-5 w-5" />
                      </button>
                    )}
                    {notification.link && (
                      <Link
                        to={notification.link}
                        className="p-2 text-primary-600 hover:text-primary-700 transition-colors"
                        title="Vai"
                      >
                        <ArrowRightIcon className="h-5 w-5" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className="text-center py-12">
            <BellIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread' ? 'Nessuna notifica non letta' : 'Nessuna notifica'}
            </p>
            <p className="text-gray-500">
              {filter === 'unread'
                ? 'Hai letto tutte le tue notifiche!'
                : 'Le notifiche appariranno qui.'}
            </p>
          </div>
        )}
      </Card>
    </div>
  )
}
